// Console — merged Inbox + Ops + Sources curator workbench.
//
// Three tabs, single page:
//   - Pending: pending changes (the old Inbox).
//   - Runs:    cron runs + score movements (the old Ops).
//   - Sources: RSS / web-feed sources we know about. Listed only — actually
//              pulling and routing items into Pending happens in a future
//              Edge Function (out of scope for v0.12).
//
// The two existing renderers (renderInbox, renderOps) are reused as-is. The
// sources tab is built fresh below.

import { renderInbox } from "./inbox.ts";
import { renderOps } from "./ops.ts";
import { getSession } from "../lib/auth.ts";
import { loadInbox, loadMovements, loadRecentRuns, type PeriodScope } from "../lib/queries.ts";

type ConsoleTab = "pending" | "runs" | "sources";

interface SourceSeed {
  name: string;
  url: string;
  category: "aggregator" | "research" | "venture" | "regulator" | "policy" | "industry";
  notes?: string;
}

// v0.12 seed list. Persisted to a `sources` table in the schema migration.
const SOURCE_SEEDS: SourceSeed[] = [
  // Aggregators (high-volume top-of-funnel)
  { name: "techurls",        url: "https://techurls.com/",                            category: "aggregator", notes: "Hacker News + TechCrunch + Verge + Wired + ~30 more in one feed." },
  { name: "Hacker News",     url: "https://news.ycombinator.com/rss",                 category: "aggregator" },
  { name: "TechCrunch",      url: "https://techcrunch.com/feed/",                     category: "aggregator" },
  { name: "Ars Technica",    url: "https://feeds.arstechnica.com/arstechnica/index",  category: "aggregator" },
  { name: "The Verge",       url: "https://www.theverge.com/rss/index.xml",           category: "aggregator" },
  // Research & analysis
  { name: "MIT Technology Review", url: "https://www.technologyreview.com/feed/",     category: "research" },
  { name: "Stratechery (free)",    url: "https://stratechery.com/feed/",              category: "research" },
  { name: "FT Alphaville",         url: "https://www.ft.com/alphaville?format=rss",   category: "research" },
  { name: "Bloomberg Tech",        url: "https://feeds.bloomberg.com/technology/news.rss", category: "research" },
  // Venture
  { name: "a16z",              url: "https://a16z.com/feed/",                          category: "venture" },
  { name: "Crunchbase News",   url: "https://news.crunchbase.com/feed/",               category: "venture" },
  { name: "CB Insights blog",  url: "https://www.cbinsights.com/research/feed",        category: "venture" },
  // Regulators / market
  { name: "Regulations.gov",   url: "https://www.regulations.gov/api/v4/documents",    category: "regulator", notes: "Topic-specific queries." },
  { name: "EUR-Lex",           url: "https://eur-lex.europa.eu/RSS",                   category: "regulator" },
  { name: "SEC press",         url: "https://www.sec.gov/news/pressreleases.rss",      category: "regulator" },
  { name: "FCA news",          url: "https://www.fca.org.uk/news/rss.xml",             category: "regulator" },
  // Policy / society
  { name: "Pew Research",      url: "https://www.pewresearch.org/feed/",               category: "policy" },
  { name: "World Economic Forum", url: "https://www.weforum.org/agenda/feed/",         category: "policy" },
  { name: "Brookings",         url: "https://www.brookings.edu/feed/",                 category: "policy" },
  // Industry-specific (BFSI lens)
  { name: "American Banker",   url: "https://www.americanbanker.com/feed",             category: "industry" },
  { name: "Fintech News",      url: "https://www.fintechnews.org/feed/",               category: "industry" },
  { name: "Insurance Journal", url: "https://www.insurancejournal.com/news/feed/",     category: "industry" },
  // Patents / publications (queried, not feed-pulled)
  { name: "USPTO",             url: "https://www.uspto.gov/",                          category: "research", notes: "Queried per topic." },
  { name: "Google Scholar",    url: "https://scholar.google.com/",                     category: "research", notes: "Queried per topic." },
  { name: "arXiv",             url: "https://arxiv.org/list/cs/new",                   category: "research" },
];

const CATEGORY_LABEL: Record<SourceSeed["category"], string> = {
  aggregator: "Aggregator",
  research: "Research",
  venture: "Venture",
  regulator: "Regulator",
  policy: "Policy / Society",
  industry: "Industry",
};

interface State {
  active: ConsoleTab;
  destroyChild: (() => void) | null;
}

export function renderConsole(
  host: HTMLElement,
  period: PeriodScope,
): () => void {
  const state: State = { active: "pending", destroyChild: null };

  function paintShell() {
    host.innerHTML = `
      <div class="console">
        <header class="console-tabs">
          <button class="console-tab ${state.active === "pending" ? "active" : ""}" data-tab="pending">
            <span class="console-tab-name">Pending</span>
            <span class="console-tab-hint">curator review</span>
          </button>
          <button class="console-tab ${state.active === "runs" ? "active" : ""}" data-tab="runs">
            <span class="console-tab-name">Runs</span>
            <span class="console-tab-hint">orchestrator + movements</span>
          </button>
          <button class="console-tab ${state.active === "sources" ? "active" : ""}" data-tab="sources">
            <span class="console-tab-name">Sources</span>
            <span class="console-tab-hint">${SOURCE_SEEDS.length} feeds</span>
          </button>
        </header>
        <div class="console-body" id="consoleBody"></div>
      </div>
    `;
    host.querySelectorAll<HTMLButtonElement>(".console-tab").forEach((b) => {
      b.addEventListener("click", () => {
        state.active = (b.dataset.tab as ConsoleTab) || "pending";
        paintShell();
        paintBody();
      });
    });
  }

  async function paintBody() {
    const body = host.querySelector<HTMLElement>("#consoleBody");
    if (!body) return;
    state.destroyChild?.();
    state.destroyChild = null;

    if (state.active === "pending") {
      try {
        const items = await loadInbox(period);
        const session = await getSession();
        state.destroyChild = renderInbox(body, items, session.role);
      } catch (e) {
        body.innerHTML = `<div class="viz-empty">Inbox load failed: ${String(e)}</div>`;
      }
    } else if (state.active === "runs") {
      try {
        const [runs, movements] = await Promise.all([loadRecentRuns(period), loadMovements(period)]);
        state.destroyChild = renderOps(body, runs, movements);
      } catch (e) {
        body.innerHTML = `<div class="viz-empty">Ops load failed: ${String(e)}</div>`;
      }
    } else {
      paintSources(body);
    }
  }

  function paintSources(body: HTMLElement) {
    const grouped = new Map<SourceSeed["category"], SourceSeed[]>();
    for (const s of SOURCE_SEEDS) {
      const arr = grouped.get(s.category) ?? [];
      arr.push(s);
      grouped.set(s.category, arr);
    }

    const sections = (Object.keys(CATEGORY_LABEL) as SourceSeed["category"][])
      .filter((c) => grouped.has(c))
      .map((c) => {
        const items = grouped.get(c)!
          .map((s) => `
            <li class="source-row">
              <div class="source-row-head">
                <span class="source-row-name">${escapeHtml(s.name)}</span>
                <span class="source-row-status">unwired</span>
              </div>
              <a class="source-row-url" href="${escapeAttr(s.url)}" target="_blank" rel="noopener">${escapeHtml(s.url)}</a>
              ${s.notes ? `<div class="source-row-notes">${escapeHtml(s.notes)}</div>` : ""}
            </li>
          `)
          .join("");
        return `
          <section class="source-group">
            <h4 class="source-group-head">${CATEGORY_LABEL[c]}</h4>
            <ul class="source-list">${items}</ul>
          </section>
        `;
      })
      .join("");

    body.innerHTML = `
      <div class="sources">
        <header class="sources-banner">
          <p>${SOURCE_SEEDS.length} sources seeded. Pulling + routing into Pending requires the <code>ingest-rss</code> Edge Function (next pass). Until then this is a config catalog.</p>
        </header>
        ${sections}
      </div>
    `;
  }

  paintShell();
  paintBody();

  return () => {
    state.destroyChild?.();
    host.innerHTML = "";
  };
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
function escapeAttr(s: string): string { return escapeHtml(s); }
